import { Dispatch } from "redux";
import {
  getAllInventory,
  createInventory,
  updateInventory,
  deleteInventory,
  uploadInventoryImage,
  IInventory,
  LowStock,
} from "../../services/inventoryApi";
import { toApiLowStock } from "../../components/lowStock.mapper";

// Action types…
export const FETCH_INVENTORY_REQUEST = "inventory/FETCH_REQUEST";
export const FETCH_INVENTORY_SUCCESS = "inventory/FETCH_SUCCESS";
export const FETCH_INVENTORY_FAILURE = "inventory/FETCH_FAILURE";
export const ADD_INVENTORY_SUCCESS = "inventory/ADD_SUCCESS";
export const UPDATE_INVENTORY_SUCCESS = "inventory/UPDATE_SUCCESS";
export const DELETE_INVENTORY_SUCCESS = "inventory/DELETE_SUCCESS";

// Helper: normalize lowStock whether it came from UI (strings) or API shape
function normalizeInventoryForApi(
  item: Partial<IInventory> & { lowStockUi?: any }
): Partial<IInventory> {
  const { lowStockUi, lowStock, ...rest } = item;

  // Prefer lowStockUi if provided (from edit modal with string fields)
  const normalizedLowStock: LowStock | undefined = toApiLowStock(
    lowStockUi ?? lowStock
  );

  return {
    ...rest,
    ...(normalizedLowStock !== undefined
      ? { lowStock: normalizedLowStock }
      : {}),
  };
}

// Thunks
export const fetchInventory = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_INVENTORY_REQUEST });
  try {
    const { data } = await getAllInventory();
    dispatch({ type: FETCH_INVENTORY_SUCCESS, payload: data });
  } catch (error: any) {
    dispatch({
      type: FETCH_INVENTORY_FAILURE,
      payload: error?.response?.data?.message || error?.message || "Failed",
    });
  }
};

export const addInventory =
  (item: Partial<IInventory> & { lowStockUi?: any }) =>
  async (dispatch: Dispatch) => {
    try {
      const payload = normalizeInventoryForApi(item);
      const { data } = await createInventory(payload);
      dispatch({ type: ADD_INVENTORY_SUCCESS, payload: data });
      return data;
    } catch (error) {
      console.error("Add inventory failed:", error);
      throw error;
    }
  };

export const updateInventoryItem =
  (id: string, item: Partial<IInventory> & { lowStockUi?: any }) =>
  async (dispatch: Dispatch) => {
    try {
      const payload = normalizeInventoryForApi(item);
      const { data } = await updateInventory(id, payload);
      dispatch({ type: UPDATE_INVENTORY_SUCCESS, payload: data });
      return data;
    } catch (error) {
      console.error("Update inventory failed:", error);
      throw error;
    }
  };

export const deleteInventoryItem =
  (id: string) => async (dispatch: Dispatch) => {
    try {
      await deleteInventory(id);
      dispatch({ type: DELETE_INVENTORY_SUCCESS, payload: id });
    } catch (error) {
      console.error("Delete inventory failed:", error);
      throw error;
    }
  };

// Keep this path consistent too: normalize lowStock before stringifying
export const uploadImageWithData =
  (
    image: File,
    itemData: Partial<IInventory> & { lowStockUi?: any },
    token: string
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const payload = normalizeInventoryForApi(itemData);
      const data = await uploadInventoryImage(image, payload, token);
      dispatch({ type: ADD_INVENTORY_SUCCESS, payload: data });
      return data;
    } catch (error) {
      console.error("Image + Inventory upload failed:", error);
      throw error;
    }
  };
